import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ChevronLeft, Camera, User, Shield, LogOut, UserCog, Settings, 
  Users, Trash2, Lock, Unlock, Plus, Edit3, Check, Key, KeyRound 
} from "lucide-react";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";

export const ProfilePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Config states
  const [cuisineTypes, setCuisineTypes] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [newCuisine, setNewCuisine] = useState("");
  const [newSpecialty, setNewSpecialty] = useState("");
  const [editingCuisine, setEditingCuisine] = useState(null);
  const [editingSpecialty, setEditingSpecialty] = useState(null);
  const [editCuisineValue, setEditCuisineValue] = useState("");
  const [editSpecialtyValue, setEditSpecialtyValue] = useState("");

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    fetchProfilePhoto();
  }, []);

  useEffect(() => {
    if (isAdmin && showAdminPanel) {
      fetchAllUsers();
    }
  }, [isAdmin, showAdminPanel]);

  useEffect(() => {
    if (isAdmin && showConfigPanel) {
      fetchCuisineTypes();
      fetchSpecialties();
    }
  }, [isAdmin, showConfigPanel]);

  const fetchProfilePhoto = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setProfilePhoto(res.data.profile_photo);
    } catch (err) {
      console.error("Error fetching profile");
    }
  };

  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/admin/users`);
      setAllUsers(res.data);
    } catch (err) {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleChangeStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "blocked" : "active";
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, { status: newStatus });
      toast.success(`Usuario ${newStatus === "active" ? "activado" : "bloqueado"}`);
      fetchAllUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar estado");
    }
  };

  // Config functions
  const fetchCuisineTypes = async () => {
    try {
      const res = await axios.get(`${API}/config/cuisine-types`);
      setCuisineTypes(res.data);
    } catch (err) {
      console.error("Error fetching cuisine types");
    }
  };

  const fetchSpecialties = async () => {
    try {
      const res = await axios.get(`${API}/config/specialties`);
      setSpecialties(res.data);
    } catch (err) {
      console.error("Error fetching specialties");
    }
  };

  const handleAddCuisine = async () => {
    if (!newCuisine.trim()) return;
    try {
      await axios.post(`${API}/config/cuisine-types`, { name: newCuisine.trim() });
      setNewCuisine("");
      fetchCuisineTypes();
      toast.success("Tipo de cocina añadido");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al añadir");
    }
  };

  const handleUpdateCuisine = async (id) => {
    if (!editCuisineValue.trim()) return;
    try {
      await axios.put(`${API}/config/cuisine-types/${id}`, { name: editCuisineValue.trim() });
      setEditingCuisine(null);
      fetchCuisineTypes();
      toast.success("Actualizado");
    } catch (err) {
      toast.error("Error al actualizar");
    }
  };

  const handleDeleteCuisine = async (id, name) => {
    if (!window.confirm(`¿Eliminar "${name}"?`)) return;
    try {
      await axios.delete(`${API}/config/cuisine-types/${id}`);
      fetchCuisineTypes();
      toast.success("Eliminado");
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const handleAddSpecialty = async () => {
    if (!newSpecialty.trim()) return;
    try {
      await axios.post(`${API}/config/specialties`, { name: newSpecialty.trim() });
      setNewSpecialty("");
      fetchSpecialties();
      toast.success("Especialidad añadida");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al añadir");
    }
  };

  const handleUpdateSpecialty = async (id) => {
    if (!editSpecialtyValue.trim()) return;
    try {
      await axios.put(`${API}/config/specialties/${id}`, { name: editSpecialtyValue.trim() });
      setEditingSpecialty(null);
      fetchSpecialties();
      toast.success("Actualizado");
    } catch (err) {
      toast.error("Error al actualizar");
    }
  };

  const handleDeleteSpecialty = async (id, name) => {
    if (!window.confirm(`¿Eliminar "${name}"?`)) return;
    try {
      await axios.delete(`${API}/config/specialties/${id}`);
      fetchSpecialties();
      toast.success("Eliminado");
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setChangingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success("Contraseña actualizada correctamente");
      setShowPasswordModal(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar contraseña");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleForcePasswordChange = async (userId, userName) => {
    if (!window.confirm(`¿Forzar cambio de contraseña para "${userName}"? El usuario deberá establecer una nueva contraseña en su próximo inicio de sesión.`)) return;
    try {
      await axios.put(`${API}/admin/users/${userId}/force-password-change`);
      toast.success("El usuario deberá cambiar su contraseña en el próximo login");
      fetchAllUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al forzar cambio de contraseña");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData);
      const photoUrl = res.data.url;
      
      await axios.put(`${API}/auth/profile-photo`, { photo_url: photoUrl });
      setProfilePhoto(photoUrl);
      toast.success("Foto de perfil actualizada");
    } catch (err) {
      toast.error("Error al subir la foto");
    } finally {
      setUploading(false);
    }
  };

  const handleChangeRole = async (userId, currentRole) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole });
      toast.success(`Rol cambiado a ${newRole}`);
      fetchAllUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar rol");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`¿Eliminar usuario "${userName}" y todos sus datos?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("Usuario eliminado");
      fetchAllUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar usuario");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("Sesión cerrada");
  };

  return (
    <div className="min-h-screen pb-24" data-testid="profile-page">
      <header className="sticky top-0 z-40 glass-nav p-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate(-1)} data-testid="back-btn">
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-xl font-semibold text-amber-500">Mi Perfil</h1>
        </div>
      </header>

      <main className="p-4 max-w-2xl mx-auto">
        <div className="glass rounded-2xl p-6 mb-6 text-center">
          <label 
            htmlFor="profile-photo-input"
            className="cursor-pointer group relative inline-block touch-manipulation"
            data-testid="profile-photo-label"
            role="button"
            tabIndex={0}
          >
            <div className="w-24 h-24 rounded-full bg-amber-500 flex items-center justify-center mx-auto mb-4 overflow-hidden relative">
              {profilePhoto ? (
                <img src={profilePhoto} alt="Perfil" className="w-full h-full object-cover pointer-events-none" />
              ) : (
                <span className="text-3xl font-bold text-black pointer-events-none">{user?.name?.[0]?.toUpperCase()}</span>
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 active:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                <Camera size={24} className="text-white" />
              </div>
              {uploading && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center pointer-events-none">
                  <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            <p className="text-xs text-zinc-500 mb-3 pointer-events-none">Toca para cambiar foto</p>
          </label>
          <input
            type="file"
            id="profile-photo-input"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoUpload}
            className="sr-only"
            data-testid="profile-photo-input"
          />
          <h2 className="text-2xl font-semibold mb-1">{user?.name}</h2>
          <p className="text-zinc-400 mb-2">{user?.email}</p>
          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
            isAdmin ? "bg-amber-500/20 text-amber-500" : "bg-zinc-700 text-zinc-300"
          }`}>
            {isAdmin ? <Shield size={12} /> : <User size={12} />}
            {isAdmin ? "Administrador" : "Usuario"}
          </span>
        </div>

        {/* Admin Panel Toggle */}
        {isAdmin && (
          <button
            onClick={() => setShowAdminPanel(!showAdminPanel)}
            className="w-full py-3 glass rounded-xl flex items-center justify-center gap-2 text-amber-500 hover:bg-amber-900/20 transition-colors mb-4"
            data-testid="admin-panel-toggle"
          >
            <UserCog size={20} /> {showAdminPanel ? "Ocultar Panel Admin" : "Panel de Administración"}
          </button>
        )}

        {/* Admin Panel */}
        {isAdmin && showAdminPanel && (
          <div className="glass rounded-2xl p-4 mb-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Users size={20} className="text-amber-500" /> Gestión de Usuarios
            </h3>
            
            {loadingUsers ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-3">
                {allUsers.map((u) => (
                  <div key={u.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center overflow-hidden">
                        {u.profile_photo ? (
                          <img src={u.profile_photo} alt={u.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-sm font-bold">{u.name?.[0]?.toUpperCase()}</span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{u.name}</p>
                        <p className="text-xs text-zinc-400">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Status badge */}
                      <span className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                        u.status === "active" ? "bg-emerald-500/20 text-emerald-500" : "bg-red-500/20 text-red-500"
                      }`}>
                        {u.status === "active" ? <Unlock size={10} /> : <Lock size={10} />}
                        {u.status === "active" ? "Activo" : "Bloqueado"}
                      </span>
                      {/* Role badge */}
                      <span className={`px-2 py-1 rounded text-xs ${
                        u.role === "admin" ? "bg-amber-500/20 text-amber-500" : "bg-zinc-700 text-zinc-400"
                      }`}>
                        {u.role === "admin" ? "Admin" : "Usuario"}
                      </span>
                      {u.id !== user?.id && (
                        <>
                          {/* Toggle status button */}
                          <button
                            onClick={() => handleChangeStatus(u.id, u.status)}
                            className={`p-2 rounded-lg transition-colors ${
                              u.status === "active" 
                                ? "bg-zinc-700 hover:bg-red-900/50 text-red-400" 
                                : "bg-zinc-700 hover:bg-emerald-900/50 text-emerald-400"
                            }`}
                            title={u.status === "active" ? "Bloquear usuario" : "Activar usuario"}
                          >
                            {u.status === "active" ? <Lock size={14} /> : <Unlock size={14} />}
                          </button>
                          {/* Force password change button */}
                          <button
                            onClick={() => handleForcePasswordChange(u.id, u.name)}
                            className={`p-2 rounded-lg transition-colors ${u.force_password_change ? "bg-orange-600 text-white" : "bg-zinc-700 hover:bg-orange-900/50 text-orange-400"}`}
                            title={u.force_password_change ? "Cambio de contraseña pendiente" : "Forzar cambio de contraseña"}
                          >
                            <KeyRound size={14} />
                          </button>
                          {/* Toggle role button */}
                          <button
                            onClick={() => handleChangeRole(u.id, u.role)}
                            className="p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-colors"
                            title={u.role === "admin" ? "Degradar a usuario" : "Promover a admin"}
                          >
                            <Shield size={14} className={u.role === "admin" ? "text-zinc-400" : "text-amber-500"} />
                          </button>
                          {/* Delete button */}
                          <button
                            onClick={() => handleDeleteUser(u.id, u.name)}
                            className="p-2 rounded-lg bg-zinc-700 hover:bg-red-900/50 text-red-400 transition-colors"
                            title="Eliminar usuario"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Config Panel Toggle */}
        {isAdmin && (
          <button
            onClick={() => setShowConfigPanel(!showConfigPanel)}
            className="w-full py-3 glass rounded-xl flex items-center justify-center gap-2 text-emerald-500 hover:bg-emerald-900/20 transition-colors mb-4"
            data-testid="config-panel-toggle"
          >
            <Settings size={20} /> {showConfigPanel ? "Ocultar Configuración" : "Configuración"}
          </button>
        )}

        {/* Config Panel */}
        {isAdmin && showConfigPanel && (
          <div className="glass rounded-2xl p-4 mb-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Settings size={20} className="text-emerald-500" /> Configuración
            </h3>
            
            {/* Cuisine Types */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-zinc-300 mb-3">Tipos de Cocina</h4>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newCuisine}
                  onChange={(e) => setNewCuisine(e.target.value)}
                  placeholder="Nuevo tipo de cocina..."
                  className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm"
                  onKeyDown={(e) => e.key === "Enter" && handleAddCuisine()}
                />
                <button
                  onClick={handleAddCuisine}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-medium transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {cuisineTypes.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
                    {editingCuisine === item.id ? (
                      <input
                        type="text"
                        value={editCuisineValue}
                        onChange={(e) => setEditCuisineValue(e.target.value)}
                        className="flex-1 px-2 py-1 bg-zinc-700 border border-zinc-600 rounded text-sm mr-2"
                        autoFocus
                        onKeyDown={(e) => e.key === "Enter" && handleUpdateCuisine(item.id)}
                      />
                    ) : (
                      <span className="text-sm">{item.name}</span>
                    )}
                    <div className="flex gap-1">
                      {editingCuisine === item.id ? (
                        <button
                          onClick={() => handleUpdateCuisine(item.id)}
                          className="p-1.5 rounded bg-emerald-600 hover:bg-emerald-500 transition-colors"
                        >
                          <Check size={12} />
                        </button>
                      ) : (
                        <button
                          onClick={() => { setEditingCuisine(item.id); setEditCuisineValue(item.name); }}
                          className="p-1.5 rounded bg-zinc-700 hover:bg-zinc-600 transition-colors"
                        >
                          <Edit3 size={12} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteCuisine(item.id, item.name)}
                        className="p-1.5 rounded bg-zinc-700 hover:bg-red-900/50 text-red-400 transition-colors"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Specialties */}
            <div>
              <h4 className="text-sm font-semibold text-zinc-300 mb-3">Especialidades</h4>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newSpecialty}
                  onChange={(e) => setNewSpecialty(e.target.value)}
                  placeholder="Nueva especialidad..."
                  className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm"
                  onKeyDown={(e) => e.key === "Enter" && handleAddSpecialty()}
                />
                <button
                  onClick={handleAddSpecialty}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-medium transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {specialties.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
                    {editingSpecialty === item.id ? (
                      <input
                        type="text"
                        value={editSpecialtyValue}
                        onChange={(e) => setEditSpecialtyValue(e.target.value)}
                        className="flex-1 px-2 py-1 bg-zinc-700 border border-zinc-600 rounded text-sm mr-2"
                        autoFocus
                        onKeyDown={(e) => e.key === "Enter" && handleUpdateSpecialty(item.id)}
                      />
                    ) : (
                      <span className="text-sm">{item.name}</span>
                    )}
                    <div className="flex gap-1">
                      {editingSpecialty === item.id ? (
                        <button
                          onClick={() => handleUpdateSpecialty(item.id)}
                          className="p-1.5 rounded bg-emerald-600 hover:bg-emerald-500 transition-colors"
                        >
                          <Check size={12} />
                        </button>
                      ) : (
                        <button
                          onClick={() => { setEditingSpecialty(item.id); setEditSpecialtyValue(item.name); }}
                          className="p-1.5 rounded bg-zinc-700 hover:bg-zinc-600 transition-colors"
                        >
                          <Edit3 size={12} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteSpecialty(item.id, item.name)}
                        className="p-1.5 rounded bg-zinc-700 hover:bg-red-900/50 text-red-400 transition-colors"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Password and Logout Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => setShowPasswordModal(true)}
            className="flex-1 py-3 glass rounded-xl flex items-center justify-center gap-2 text-amber-500 hover:bg-amber-900/20 transition-colors"
            data-testid="change-password-btn"
          >
            <Key size={20} /> Cambiar Contraseña
          </button>
          <button
            onClick={handleLogout}
            className="flex-1 py-3 glass rounded-xl flex items-center justify-center gap-2 text-red-400 hover:bg-red-900/20 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={20} /> Cerrar Sesión
          </button>
        </div>

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 rounded-2xl p-6 w-full max-w-md border border-zinc-700">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Key size={24} className="text-amber-500" /> Cambiar Contraseña
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="form-label">Contraseña Actual</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="form-input"
                    placeholder="Tu contraseña actual"
                    data-testid="current-password-input"
                  />
                </div>
                <div>
                  <label className="form-label">Nueva Contraseña</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="form-input"
                    placeholder="Mínimo 6 caracteres"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <label className="form-label">Confirmar Nueva Contraseña</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="form-input"
                    placeholder="Repite la nueva contraseña"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                  }}
                  className="flex-1 py-3 glass rounded-xl text-zinc-400 hover:bg-zinc-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleChangePassword}
                  disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                  className="flex-1 py-3 bg-amber-500 text-black font-semibold rounded-xl hover:bg-amber-400 transition-colors disabled:opacity-50"
                  data-testid="save-password-btn"
                >
                  {changingPassword ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ProfilePage;
